import React, { useCallback, useState } from 'react';
import _get from 'lodash/get';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import TextField from '@mui/material/TextField';
import _isEmpty from 'lodash/isEmpty';
import { Text } from '@lifeomic/chroma-react/components/Text';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import Section from './Section';
import JSONDisplay from './JSONDisplay';
import { Link } from '@lifeomic/chroma-react/components/Link';
import clsx from 'clsx';
import { Box } from '@lifeomic/chroma-react/components/Box';
import JMESParser, {
  fhirTypesAsSelectOptions,
} from '../../../../../util/JMESParser';
import {
  ParserSupportedFhirTypes,
  JmesPath,
} from '../../../../../util/JMESParser/jmesFhirTypes';

const messages = defineMessages({
  jmesField: {
    id: 'jmesEditor.jmesField',
    defaultMessage: 'JMESPath Query',
  },
  outputType: {
    id: 'jmesEditor.outputType',
    defaultMessage: 'Output Format Type',
  },
  inputHeading: {
    id: 'jmesEditor.inputHeading',
    defaultMessage: 'Input',
  },
  inputSubHeading: {
    id: 'jmesEditor.inputSubHeading',
    defaultMessage:
      'The following JSON is the input data to your JMESPath query.',
  },
  invalidInput: {
    id: 'jmesExplorer.inputError',
    defaultMessage: 'The input JSON is not valid.',
  },
  outputHeading: {
    id: 'jmesEditor.outputHeading',
    defaultMessage: 'Output',
  },
  noQueryResults: {
    id: 'jmesEditor.noQueryResults',
    defaultMessage: 'Your JMESPath query returned no results.',
  },
  formattedResultHeader: {
    id: 'jmesEditor.formattedResultHeader',
    defaultMessage: 'Formatted Result',
  },
  noData: {
    id: 'jmesEditor.noData',
    defaultMessage: 'Could not find any matching data for this patient.',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    maxHeight: '100%',
    height: '100%',
  },
  explorerContentRoot: {
    padding: theme.spacing(1),
    paddingBottom: 0,
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
  },
  displaySection: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  subtitle: {
    padding: theme.spacing(2),
  },
  inputLabel: {
    marginTop: theme.spacing(2),
  },
  whiteBackground: {
    background: theme.palette.common.white,
  },
  jmesPathField: {
    marginTop: theme.spacing(2),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  typeContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  outputContainer: {
    flex: 1,
    width: '100%',
    overflowY: 'auto',
  },
  typedResultSection: {
    height: 300,
    width: '100%',
  },
  typedResultContainer: {
    maxWidth: '50%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    height: 200,
  },
  typedResultLabel: {
    marginTop: theme.spacing(6),
  },
  typeSelect: {
    width: 200,
  },
  rightMargin: {
    marginRight: theme.spacing(2),
  },
  error: {
    color: theme.palette.red.main,
  },
}));

export type ExplorerClasses = GetClasses<typeof useStyles>;

export interface ExplorerOwnProps {
  jmesParser: JMESParser;
  onClose: () => void;
  isEditorOpen: boolean;
}

export type ExplorerProps = ExplorerOwnProps;

const Explorer = (props: ExplorerProps) => {
  const formatMessage = useIntl().formatMessage;
  const classes = useStyles(props);
  const [jmesPath, setJmesPath] = useState<JmesPath>(
    _get<JMESParser, 'jmesSearchPath', JmesPath>(
      props.jmesParser,
      'jmesSearchPath',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    ),
  );
  const [outputType, setOutputType] = useState<ParserSupportedFhirTypes>(
    _get<JMESParser, 'outputType', ParserSupportedFhirTypes>(
      props.jmesParser,
      'outputType',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    ),
  );

  const onJMESPathChange = useCallback(
    (event: any) => {
      const newValue = _get(event, 'target.value', '');
      setJmesPath(newValue);
    },
    [setJmesPath],
  );

  const onOutputTypeChange = useCallback(
    (event: any) => {
      const newValue = _get(event, 'target.value', '');
      setOutputType(newValue);
    },
    [setOutputType],
  );

  const { jmesParser, isEditorOpen } = props;

  const noData = (
    <Box fullWidth fullHeight align="center" justify="center">
      <Text>{formatMessage(messages.noData)}</Text>
    </Box>
  );

  if (!isEditorOpen || !jmesParser) return noData;

  const parser = new JMESParser(
    jmesParser.jmesSearchInput,
    jmesPath,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    _isEmpty(outputType) ? null : outputType,
    jmesParser.momentFormat,
    jmesParser.formatMessage,
  );

  const untypedResult = _isEmpty(jmesPath)
    ? jmesParser.jmesSearchInput
    : parser.untypedParse();

  const typedResult = _isEmpty(jmesPath)
    ? null
    : parser.parseToRenderableValue();

  const renderTypedResult = () => {
    if (!_isEmpty(typedResult)) {
      return <Text>{String(typedResult)}</Text>;
    } else {
      return (
        <Text className={classes.error}>
          <FormattedMessage
            id="jmesExplorer.noTypedResult"
            defaultMessage="No Formatted Result"
          />
        </Text>
      );
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.explorerContentRoot}>
        <div className={clsx(classes.contentColumn, classes.rightMargin)}>
          <Section
            className={classes.displaySection}
            headingIntlMessage={messages.inputHeading}
            subHeadingIntlMessage={messages.inputSubHeading}
          >
            <JSONDisplay
              displayInput={jmesParser.jmesSearchInput}
              invalidIntlMessage={messages.invalidInput}
            />
          </Section>
        </div>
        <div className={classes.contentColumn}>
          <Section
            className={classes.outputContainer}
            headingIntlMessage={messages.outputHeading}
            subHeadingMessage={
              <FormattedMessage
                id="jmesEditor.outputSubHeading"
                defaultMessage="The JMESPath query and the output JSON from executing the query. {link}"
                values={{
                  link: (
                    <Link to="http://jmespath.org/examples.html" newTab>
                      View JMESPath query documentation
                    </Link>
                  ),
                }}
              />
            }
          >
            <TextField
              label={formatMessage(messages.jmesField)}
              value={jmesPath}
              onChange={onJMESPathChange}
              className={classes.jmesPathField}
              variant="outlined"
              InputProps={{ classes: { root: classes.whiteBackground } }}
            />
            <Text size="caption">
              <FormattedMessage
                id="jmesExplorer.results"
                defaultMessage="Query Results"
              />
            </Text>
            <JSONDisplay
              displayInput={untypedResult}
              invalidIntlMessage={messages.noQueryResults}
            />
          </Section>
          <Section
            headingIntlMessage={messages.formattedResultHeader}
            subHeadingMessage={
              <FormattedMessage
                id="jmesExplorer.formatSubhead"
                defaultMessage="JMESPath query results can be formatted to a more human-readable format for supported FHIR types. {link}"
                values={{
                  link: (
                    <Link
                      to="https://www.hl7.org/fhir/STU3/datatypes.html"
                      newTab
                    >
                      View documentation for FHIR types
                    </Link>
                  ),
                }}
              />
            }
          >
            <div className={classes.typeContainer}>
              <FormControl>
                <InputLabel htmlFor="type-selection-dropdown">
                  {formatMessage(messages.outputType)}
                </InputLabel>
                <Select
                  className={classes.typeSelect}
                  input={
                    <Input name="result-type" id="type-selection-dropdown" />
                  }
                  value={outputType || ''}
                  onChange={onOutputTypeChange}
                >
                  <MenuItem value="">{'None'}</MenuItem>
                  {fhirTypesAsSelectOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.typedResultContainer}>
                <Text size="caption">
                  <FormattedMessage
                    id="jmesExplorer.typeResultLabel"
                    defaultMessage="Formatted Result"
                  />
                </Text>
                {renderTypedResult()}
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Explorer);
